import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { CoverBadge } from './CoverBadge'
import { getShadowColor, SHADOW_OFFSET } from './coverHelpers'
import { MediaGroup } from './MediaGroup'

type ImageMode = 'square' | 'fill'

interface Props {
  title: string
  bg: string
  color: string
  titleSize: string
  titleFontWeight: number | string
  cardImage: string | null
  authorName?: string | null
  authorAvatar?: string | null
  viewsCount: number
  badgeSize: 'sm' | 'lg'
  imageMode: ImageMode
  onMediaClick?: () => void
  placeholder?: ReactNode
  className?: string
}

export const Type2Square = ({
  title,
  bg,
  color,
  titleSize,
  titleFontWeight,
  cardImage,
  authorName,
  authorAvatar,
  viewsCount,
  badgeSize,
  imageMode,
  onMediaClick,
  placeholder,
  className,
}: Props) => {
  const m = useMessages(messages)
  const shadow = getShadowColor(bg)
  return (
    <div
      className={cn(
        'w-full rounded-xl overflow-hidden border border-border shadow-md relative flex flex-col',
        className,
      )}
      style={{ background: bg }}
    >
      <div
        className={cn(
          'w-full flex items-center justify-center p-5 pb-2',
          imageMode === 'square' ? 'aspect-square' : 'flex-1 min-h-0',
        )}
      >
        <div
          className="aspect-square"
          style={{
            width: '84%',
            transform: `translate(${(SHADOW_OFFSET * 100) / 2}%, -${(SHADOW_OFFSET * 100) / 2}%)`,
          }}
        >
          <MediaGroup
            size="100%"
            image={cardImage}
            shadow={shadow}
            onMediaClick={onMediaClick}
            placeholder={placeholder}
          />
        </div>
      </div>
      <div className="px-4 pb-5 pt-0 space-y-2.5 -mt-2">
        <div
          className="w-full flex items-center justify-center"
          style={{
            minHeight: imageMode === 'square' ? 'clamp(48.3px, 11.73vw, 96.6px)' : undefined,
          }}
        >
          <h3
            className="leading-[1.15] line-clamp-3 text-center w-full"
            style={{
              fontFamily: '"Wix Madefor Display", system-ui, sans-serif',
              fontWeight: titleFontWeight,
              color,
              fontSize: titleSize,
            }}
          >
            {title || m.untitled}
          </h3>
        </div>
        <CoverBadge
          authorName={authorName}
          authorAvatar={authorAvatar}
          viewsCount={viewsCount}
          size={badgeSize}
        />
      </div>
    </div>
  )
}
